import type { IdeaIssueSnapshot } from '@steward/shared';
import type { OrderTicket } from '@/lib/formatOrderTicket';

export type EngineStrategy = 'putSpread' | 'callSpread' | 'ironCondor';

export interface SpreadSide {
  optionType: 'put' | 'call';
  shortStrike: number;
  longStrike: number;
  shortBid: number | null;
  shortAsk: number | null;
  shortMark: number | null;
  longMark: number | null;
}

export interface Idea {
  strategy: EngineStrategy;
  strategyLabel: string;        // e.g. "Vertical Put Spread" — used for the copy ticket
  expiration: string;           // YYYY-MM-DD
  dte: number;
  credit: number;               // dollars for one spread, as returned by the engine
  widthPoints: number;          // max side width in strike points
  maxRisk: number;              // dollars
  returnOnRisk: number;
  shortDelta: number | null;    // representative short delta (put side for IC)
  shortIV: number | null;
  sides: SpreadSide[];          // 1 for a vertical, 2 for an iron condor
}

export interface Fingerprint {
  avgHoldDays: number;
  winRate: number;              // 0-100
  winCount: number;
  sampleSize: number;
  lowConfidence: boolean;       // sampleSize < 3
}

export interface Candidate {
  symbol: string;
  reasons: Array<'dormant' | 'top_performer'>;
  rationale: string;
  totalPnl: number;
  dominantStrategyType: string | null;        // raw StrategyType from history
  dominantEngineStrategy: EngineStrategy | null;
  fingerprint: Fingerprint | null;
  eager: boolean;
}

export interface HeadlineIdeas {
  primary: Idea | null;
  secondary: Idea | null;
}

export interface LiquidityResult {
  ok: boolean;
  reason?: string;
}

// Minimal shape consumed from /analytics/symbol-insights `symbols[]`.
export interface SymbolChain {
  id: string;
  strategyType: string;
  status: string;
  openedAt: Date | string;
  closedAt: Date | string | null;
  realizedPnl: number;
  netPremium: number;
}
export interface SymbolInsight {
  symbol: string;
  openCount: number;
  closedCount: number;
  totalPnl: number;
  winRate: number | null;
  daysSinceLastTrade: number;
  strategies: string[];
  chains: SymbolChain[];
}

const STRATEGY_MAP: Record<string, EngineStrategy> = {
  iron_condor: 'ironCondor',
  vertical_spread: 'putSpread', // history can't distinguish put vs call; default to put, fall back handled in pickHeadlineIdeas
};

/** Map a stored StrategyType to the suggestion engine's strategy key, or null if no clean equivalent. */
export function mapStrategy(strategyType: string): EngineStrategy | null {
  return STRATEGY_MAP[strategyType] ?? null;
}

/** Credit relative to capital at risk. -Infinity when risk is non-positive (bad quote) so it never ranks first. */
export function returnOnRisk(credit: number, widthPoints: number): number {
  const maxRisk = widthPoints * 100 - credit;
  if (maxRisk <= 0) return -Infinity;
  return credit / maxRisk;
}

const MS_PER_DAY = 1000 * 60 * 60 * 24;

/** Most frequent strategyType; ties broken by most recent. Assumes `chains` is newest-first. */
export function dominantStrategyType(chains: SymbolChain[]): string | null {
  if (chains.length === 0) return null;
  const counts = new Map<string, number>();
  const firstSeenIndex = new Map<string, number>();
  chains.forEach((c, i) => {
    counts.set(c.strategyType, (counts.get(c.strategyType) ?? 0) + 1);
    if (!firstSeenIndex.has(c.strategyType)) firstSeenIndex.set(c.strategyType, i);
  });
  let best: string | null = null;
  let bestCount = -1;
  let bestIndex = Infinity;
  for (const [type, count] of counts) {
    const idx = firstSeenIndex.get(type)!;
    if (count > bestCount || (count === bestCount && idx < bestIndex)) {
      best = type;
      bestCount = count;
      bestIndex = idx;
    }
  }
  return best;
}

/** Tier-0 fingerprint from closed winners only, using fields available in symbol-insights. */
export function buildFingerprint(chains: SymbolChain[]): Fingerprint | null {
  const closed = chains.filter((c) => c.status === 'closed');
  const winners = closed.filter((c) => c.realizedPnl > 0);
  if (winners.length === 0) return null;

  const holdDays = winners
    .filter((c) => c.closedAt)
    .map((c) => (new Date(c.closedAt!).getTime() - new Date(c.openedAt).getTime()) / MS_PER_DAY);
  const avgHoldDays = holdDays.length
    ? holdDays.reduce((a, b) => a + b, 0) / holdDays.length
    : 0;

  const winRate = closed.length ? (winners.length / closed.length) * 100 : 0;

  return {
    avgHoldDays,
    winRate,
    winCount: winners.length,
    sampleSize: winners.length,
    lowConfidence: winners.length < 3,
  };
}

const TOP_PERFORMER_COUNT = 10;
const MAX_CANDIDATES = 20;
const EAGER_COUNT = 6;

function formatUsd(n: number): string {
  const sign = n < 0 ? '-' : '+';
  return `${sign}$${Math.abs(Math.round(n)).toLocaleString()}`;
}

/** Pick + rank candidate symbols for Trade Ideas. */
export function selectCandidates(symbols: SymbolInsight[]): Candidate[] {
  // Top-performer set: highest totalPnl, positive only.
  const topSet = new Set(
    [...symbols]
      .filter((s) => s.totalPnl > 0)
      .sort((a, b) => b.totalPnl - a.totalPnl)
      .slice(0, TOP_PERFORMER_COUNT)
      .map((s) => s.symbol)
  );

  const ranked = symbols
    .filter((s) => s.openCount === 0)
    .map((s) => {
      const reasons: Candidate['reasons'] = [];
      const isDormant = s.daysSinceLastTrade > 60 && s.closedCount >= 3 && s.totalPnl > 0;
      if (isDormant) reasons.push('dormant');
      if (topSet.has(s.symbol)) reasons.push('top_performer');
      return { s, reasons };
    })
    .filter(({ reasons }) => reasons.length > 0)
    .sort((a, b) => b.s.totalPnl - a.s.totalPnl);

  if (ranked.length > MAX_CANDIDATES) {
    console.warn(`[tradeIdeas] candidate list truncated to ${MAX_CANDIDATES}`);
  }
  const candidates = ranked.slice(0, MAX_CANDIDATES);

  return candidates.map(({ s, reasons }, i) => {
    const rawStrategy = dominantStrategyType(s.chains);
    const parts: string[] = [];
    if (reasons.includes('dormant')) {
      parts.push(`dormant ${s.daysSinceLastTrade}d`);
    } else if (reasons.includes('top_performer')) {
      parts.push('top performer');
    }
    parts.push(formatUsd(s.totalPnl));
    if (s.winRate != null) parts.push(`${Math.round(s.winRate)}% win`);

    return {
      symbol: s.symbol,
      reasons,
      rationale: parts.join(' · '),
      totalPnl: s.totalPnl,
      dominantStrategyType: rawStrategy,
      dominantEngineStrategy: rawStrategy ? mapStrategy(rawStrategy) : null,
      fingerprint: buildFingerprint(s.chains),
      eager: i < EAGER_COUNT,
    };
  });
}

interface ApiSpread {
  expiration: string;
  dte: number;
  shortStrike: number;
  longStrike: number;
  shortDelta: number;
  shortIV: number | null;
  credit: number;
  spreadWidth: number;
  shortBid?: number | null;
  shortAsk?: number | null;
  shortMark?: number | null;
  longMark?: number | null;
}
interface ApiSuggestion {
  expiration: string;
  dte: number;
  putSpread: ApiSpread | null;
  callSpread: ApiSpread | null;
  ironCondor: { putSide: ApiSpread; callSide: ApiSpread; totalCredit: number } | null;
}
export interface TradeSuggestionsResponse {
  symbol: string;
  underlyingPrice: number;
  spreadWidth: number;
  quantity: number;
  source?: string;            // 'schwab' | 'tradier' | 'yahoo'; backend already returns this
  suggestions: ApiSuggestion[];
}

function sideFrom(s: ApiSpread, optionType: 'put' | 'call'): SpreadSide {
  return {
    optionType,
    shortStrike: s.shortStrike,
    longStrike: s.longStrike,
    shortBid: s.shortBid ?? null,
    shortAsk: s.shortAsk ?? null,
    shortMark: s.shortMark ?? null,
    longMark: s.longMark ?? null,
  };
}

/** Convert a trade-suggestions response into one normalized Idea per (strategy, expiration). */
export function flattenIdeas(data: TradeSuggestionsResponse): Idea[] {
  const ideas: Idea[] = [];
  for (const s of data.suggestions) {
    if (s.putSpread) {
      ideas.push({
        strategy: 'putSpread', strategyLabel: 'Vertical Put Spread',
        expiration: s.expiration, dte: s.dte,
        credit: s.putSpread.credit, widthPoints: s.putSpread.spreadWidth,
        maxRisk: s.putSpread.spreadWidth * 100 - s.putSpread.credit,
        returnOnRisk: returnOnRisk(s.putSpread.credit, s.putSpread.spreadWidth),
        shortDelta: s.putSpread.shortDelta, shortIV: s.putSpread.shortIV,
        sides: [sideFrom(s.putSpread, 'put')],
      });
    }
    if (s.callSpread) {
      ideas.push({
        strategy: 'callSpread', strategyLabel: 'Vertical Call Spread',
        expiration: s.expiration, dte: s.dte,
        credit: s.callSpread.credit, widthPoints: s.callSpread.spreadWidth,
        maxRisk: s.callSpread.spreadWidth * 100 - s.callSpread.credit,
        returnOnRisk: returnOnRisk(s.callSpread.credit, s.callSpread.spreadWidth),
        shortDelta: s.callSpread.shortDelta, shortIV: s.callSpread.shortIV,
        sides: [sideFrom(s.callSpread, 'call')],
      });
    }
    if (s.ironCondor) {
      const width = Math.max(s.ironCondor.putSide.spreadWidth, s.ironCondor.callSide.spreadWidth);
      ideas.push({
        strategy: 'ironCondor', strategyLabel: 'Iron Condor',
        expiration: s.expiration, dte: s.dte,
        credit: s.ironCondor.totalCredit, widthPoints: width,
        maxRisk: width * 100 - s.ironCondor.totalCredit,
        returnOnRisk: returnOnRisk(s.ironCondor.totalCredit, width),
        shortDelta: s.ironCondor.putSide.shortDelta, shortIV: s.ironCondor.putSide.shortIV,
        sides: [sideFrom(s.ironCondor.putSide, 'put'), sideFrom(s.ironCondor.callSide, 'call')],
      });
    }
  }
  return ideas;
}

export interface ReviseBase {
  underlyingSymbol: string;
  spreadWidth?: number;
  quantity?: number;
  targetDeltaMin?: number;
  targetDeltaMax?: number;
  sharesHeld?: number;
}
export interface ReviseOverrides {
  spreadWidth?: number;
  targetDeltaMin?: number;
  targetDeltaMax?: number;
}

/** Pick the already-fetched Idea for a strategy+expiration, or null if absent. No network. */
export function selectSlice(
  response: TradeSuggestionsResponse,
  sel: { strategy: EngineStrategy; expiration: string },
): Idea | null {
  return (
    flattenIdeas(response).find(
      (i) => i.strategy === sel.strategy && i.expiration === sel.expiration,
    ) ?? null
  );
}

/** Merge defined overrides onto a base trade-suggestions request (drops undefined values). */
export function reviseRequest(base: ReviseBase, overrides: ReviseOverrides): ReviseBase {
  const defined = Object.fromEntries(
    Object.entries(overrides).filter(([, v]) => v !== undefined),
  );
  return { ...base, ...defined };
}

const WIDE_THRESHOLD = 0.30;

/** Flag ideas that are likely hard to fill or have stale/arbitrage-violating quotes. */
export function liquidityFlag(idea: Idea): LiquidityResult {
  for (const side of idea.sides) {
    const { shortBid, shortAsk, shortMark, longMark } = side;
    // Crossed market.
    if (shortBid != null && shortAsk != null && shortBid > shortAsk) {
      return { ok: false, reason: 'stale quotes — value unreliable' };
    }
    // Wide bid/ask relative to mark.
    // shortMark ≈ mid; used as the wide-spread denominator (spec wrote (ask-bid)/mid)
    if (shortBid != null && shortAsk != null && shortMark != null && shortMark > 0) {
      if ((shortAsk - shortBid) / shortMark > WIDE_THRESHOLD) {
        return { ok: false, reason: 'wide bid/ask — may be hard to fill' };
      }
    }
    // Inverted/stale: in a credit spread the short leg must mark richer than the long.
    if (shortMark != null && longMark != null && longMark > shortMark) {
      return { ok: false, reason: 'stale quotes — value unreliable' };
    }
  }
  return { ok: true };
}

const TARGET_DTE = 35;
const ALL_STRATEGIES: EngineStrategy[] = ['putSpread', 'callSpread', 'ironCondor'];

function nearestDte(ideas: Idea[], target: number): Idea | null {
  if (ideas.length === 0) return null;
  return ideas.reduce((best, i) =>
    Math.abs(i.dte - target) < Math.abs(best.dte - target) ? i : best
  );
}

/** Two headline ideas: history-matched primary + best return-on-risk secondary (never the same strategy). */
export function pickHeadlineIdeas(ideas: Idea[], dominant: EngineStrategy | null): HeadlineIdeas {
  // One representative idea per strategy: the one nearest the target DTE.
  const perStrategy = ALL_STRATEGIES
    .map((s) => nearestDte(ideas.filter((i) => i.strategy === s), TARGET_DTE))
    .filter((i): i is Idea => i !== null);

  const primary = dominant ? perStrategy.find((i) => i.strategy === dominant) ?? null : null;
  const rest = perStrategy.filter((i) => i !== primary);
  const secondary = rest.length
    ? rest.reduce((best, i) => (i.returnOnRisk > best.returnOnRisk ? i : best))
    : null;

  return { primary, secondary };
}

/** Build a copy-paste order ticket (STO short / BTO long per side). */
export function ideaToTicket(idea: Idea, symbol: string): OrderTicket {
  const legs = idea.sides.flatMap((side) => [
    { action: 'STO' as const, quantity: 1, symbol, expiration: idea.expiration, strike: side.shortStrike, optionType: side.optionType },
    { action: 'BTO' as const, quantity: 1, symbol, expiration: idea.expiration, strike: side.longStrike, optionType: side.optionType },
  ]);
  return {
    symbol,
    strategy: idea.strategyLabel,
    legs,
    netCredit: idea.credit / 100,
  };
}

/** Snapshot the displayed idea for a data-quality report. `capturedAt` is passed in (keeps this pure). */
export function buildIssueSnapshot(
  idea: Idea,
  symbol: string,
  underlyingPrice: number,
  source: string,
  capturedAt: Date,
): IdeaIssueSnapshot {
  return {
    symbol,
    strategy: idea.strategy,
    expiration: idea.expiration,
    dte: idea.dte,
    credit: idea.credit,
    returnOnRisk: idea.returnOnRisk,
    widthPoints: idea.widthPoints,
    underlyingPrice,
    source,
    capturedAt,
    sides: idea.sides.map((s, i) => ({
      optionType: s.optionType,
      shortStrike: s.shortStrike,
      longStrike: s.longStrike,
      shortBid: s.shortBid,
      shortAsk: s.shortAsk,
      shortMark: s.shortMark,
      longMark: s.longMark,
      shortDelta: i === 0 ? idea.shortDelta : undefined,
      shortIV: i === 0 ? idea.shortIV : undefined,
    })),
  };
}
